import React, { useEffect, useState } from "react";
import {
  terminalsFormReducer,
  terminalsFormMapStateToProps,
  terminalsFormMapDispatchToProps,
} from "./TerminalsForm.state";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  TerminalResponse,
  ErrorWithMessage,
  FormType,
  ReduxFreeformActions,
  ReduxStoreRoot,
  MatchOnRouteId,
  Pages,
} from "../../types";
import { useClearFormOnUnmount, useSetFormFieldsState } from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFunction,
  getErrorDictionary,
  getSelectOptionsWithPlaceholder,
} from "../../util";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ButtonWithAction,
  Cluster,
  FormInput,
  FormSelect,
  Loading,
  Title,
} from "@thecb/components";
import { FormContainer, Navigation } from "../..";
import Terminal from "../../types/api/models/terminal";
import { RequestMethod } from "../../types/api/RequestMethod";
import { AlertVariants } from "../../types/Alert";
import { ALERTS, CREATE } from "../../util/constants";
import { replacePOSAlerts } from "../../state/root";

interface Props {
  actions: {
    forms: {
      terminalsForm: {
        actions: ReduxFreeformActions;
      };
    };
  };
}

const TerminalsForm = ({ actions }: Props) => {
  const formActions = actions.forms.terminalsForm.actions;
  useClearFormOnUnmount(formActions.form);
  const dispatch = useDispatch();
  const history = useHistory();
  const [formType, setFormType] = useState<FormType>(FormType.EDIT);
  const { terminalsForm } = useSelector(
    (state: ReduxStoreRoot) => state.posConfigTool.terminals.forms
  );
  const { posApiEndpoint } = useSelector((state: ReduxStoreRoot) => state.config);
  const {
    client: {
      value: { brand: selectedBrand },
    },
  } = useSelector((state: ReduxStoreRoot) => state.currentUser);
  const match: MatchOnRouteId | null = useRouteMatch("/pos-config/terminals/:id");
  const terminalId = match?.params?.id;
  const { data: editTerminalData, isFetching: isFetchingView } = useQuery<
    TerminalResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /terminals/${terminalId}`],
    queryFn: () => fetchFunction(`${posApiEndpoint}/terminals/${terminalId}/edit`),
    enabled: terminalId !== FormType.CREATE,
  });

  const { data: createTerminalData, isFetching: isFetchingCreate } = useQuery<
    TerminalResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /terminals${selectedBrand}/new`],
    queryFn: () =>
      fetchFunction(`${posApiEndpoint}/terminals/new?brand=${selectedBrand}`),
    enabled: formType === FormType.CREATE,
  });

  const { mutate: createTerminal, isLoading: isLoadingPOST } = useMutation<
    TerminalResponse,
    ErrorWithMessage,
    { terminal: Partial<Terminal> }
  >({
    mutationFn: (body: { terminal: Partial<Terminal> }) =>
      fetchFunction(`${posApiEndpoint}/terminals`, {
        method: RequestMethod.POST,
        body,
      }),
    onSuccess: (res: TerminalResponse) => {
      const newTerminalLink = history.location.pathname.replace(
        CREATE,
        `${res.data.id}/edit`
      );
      history.push(newTerminalLink);
      setFormType(FormType.EDIT);
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.name} Successfully Created`,
          alertClearTimeout: 3000,
        })
      );
    },
  });

  useSetFormFieldsState(formActions, editTerminalData);

  const {
    mutate: updateTerminal,
    isLoading: isLoadingPUT,
    data: updateTerminalResponse,
  } = useMutation<
    TerminalResponse,
    ErrorWithMessage,
    { terminal: Partial<Terminal> }
  >({
    mutationFn: (body: { terminal: Partial<Terminal> }) =>
      fetchFunction(`${posApiEndpoint}/terminals/${body.terminal.id}`, {
        method: RequestMethod.PUT,
        body,
      }),
    onSuccess: (res: TerminalResponse) =>
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.name} Successfully Updated`,
          alertClearTimeout: 3000,
        })
      ),
  });

  useSetFormFieldsState(formActions, updateTerminalResponse);
  useSetFormFieldsState(formActions, createTerminalData);

  useEffect(() => {
    if (terminalId === FormType.CREATE) {
      setFormType(FormType.CREATE);
    }
  }, [terminalId]);

  if (editTerminalData || createTerminalData) {
    return (
      <>
        <Title as="h1" weight="bold">
          Terminals
        </Title>
        <Navigation page={Pages.TERMINALS} />
        {formType === FormType.CREATE ? (
          <Title as="h1" weight="bold" extraStyles={`margin: 0.5rem 0`}>
            Create Terminal
          </Title>
        ) : (
          ""
        )}
        <FormContainer header={editTerminalData?.data.name || ""}>
          <Title
            as="h2"
            variant="small"
            weight="600"
            extraStyles={`margin-bottom: 0.5rem`}
          >
            Terminal Settings
          </Title>
          <FormSelect
            labelTextWhenNoError="Location"
            errorMessages={getErrorDictionary("Location")}
            options={getSelectOptionsWithPlaceholder(
              (editTerminalData?.data.locations ||
                createTerminalData?.data.locations) ??
                [],
              terminalsForm.location_id.rawValue
            )}
            field={terminalsForm.location_id}
            fieldActions={formActions.fields.location_id}
          />
          <FormInput
            labelTextWhenNoError="Name"
            errorMessages={getErrorDictionary("Name")}
            fieldActions={formActions.fields.name}
            field={terminalsForm.name}
          />
          <FormInput
            labelTextWhenNoError="Payment source"
            errorMessages={getErrorDictionary("Payment source")}
            fieldActions={formActions.fields.payment_source}
            field={terminalsForm.payment_source}
          />
          <FormInput
            labelTextWhenNoError="Kiosk ID"
            errorMessages={getErrorDictionary("Kiosk ID")}
            fieldActions={formActions.fields.kiosk_id}
            field={terminalsForm.kiosk_id}
          />
          <FormSelect
            fieldActions={formActions.fields.credit_card_reader_type_id}
            labelTextWhenNoError="Credit card reader type (Optional - takes precedence over client value)"
            errorMessages={getErrorDictionary("Credit card reader type")}
            field={terminalsForm.credit_card_reader_type_id}
            options={getSelectOptionsWithPlaceholder(
              (editTerminalData?.data.credit_card_reader_types ||
                createTerminalData?.data.credit_card_reader_types) ??
                [],
              terminalsForm.credit_card_reader_type_id.rawValue,
              true
            )}
          />
          <FormInput
            labelTextWhenNoError="Internal Device Identifier (Optional)"
            errorMessages={getErrorDictionary("Internal Device Identifier")}
            fieldActions={formActions.fields.internal_device_id}
            field={terminalsForm.internal_device_id}
          />
          <FormSelect
            fieldActions={formActions.fields.read_only}
            labelTextWhenNoError="Read Only"
            errorMessages={getErrorDictionary("Read Only")}
            field={terminalsForm.read_only}
            options={getSelectOptionsWithPlaceholder(
              [
                { text: "True", value: "true" },
                { text: "False", value: "false" },
              ],
              terminalsForm.read_only.rawValue
            )}
          />
          <Cluster justify="flex-end">
            <ButtonWithAction
              disabled={Object.values(terminalsForm).some(
                (formValue) => formValue?.hasErrors
              )}
              extraStyles="margin: 0;"
              text={
                formType === FormType.CREATE ? "Create Terminal" : "Save Changes"
              }
              action={(e) => {
                e.preventDefault();
                if (formType === FormType.EDIT && editTerminalData) {
                  updateTerminal({
                    terminal: {
                      id: editTerminalData.data.id,
                      location_id: terminalsForm.location_id.rawValue,
                      name: terminalsForm.name.rawValue,
                      payment_source: terminalsForm.payment_source.rawValue,
                      kiosk_id: terminalsForm.kiosk_id.rawValue,
                      credit_card_reader_type_id:
                        terminalsForm.credit_card_reader_type_id.rawValue,
                      internal_device_id: terminalsForm.internal_device_id.rawValue,
                      read_only: terminalsForm.read_only.rawValue === "true",
                    },
                  });
                }
                if (formType === FormType.CREATE && createTerminalData) {
                  createTerminal({
                    terminal: {
                      location_id: terminalsForm.location_id.rawValue,
                      name: terminalsForm.name.rawValue,
                      payment_source: terminalsForm.payment_source.rawValue,
                      kiosk_id: terminalsForm.kiosk_id.rawValue,
                      credit_card_reader_type_id:
                        terminalsForm.credit_card_reader_type_id.rawValue,
                      internal_device_id: terminalsForm.internal_device_id.rawValue,
                      read_only: terminalsForm.read_only.rawValue === "true",
                    },
                  });
                }
              }}
            />
          </Cluster>
        </FormContainer>
      </>
    );
  }

  if (isFetchingView || isFetchingCreate || isLoadingPUT || isLoadingPOST) {
    return <Loading />;
  }

  return null;
};

TerminalsForm.reducer = terminalsFormReducer;
TerminalsForm.mapStateToProps = terminalsFormMapStateToProps;
TerminalsForm.mapDispatchToProps = terminalsFormMapDispatchToProps;

export default TerminalsForm;
